import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useWoSpareConsumptionReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/spare-cost")({
  component: SpareCostReport,
});

function SpareCostReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [search, setSearch] = useState("");
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;

  const { data, isLoading } = useWoSpareConsumptionReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    search: search || null,
  });
  const rows = data ?? [];

  const totalCost = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0)) * Number(r.unit_cost ?? 0), 0),
    [rows],
  );

  const exportRows = useMemo(() => () => rows.map((r) => {
    const net = Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0);
    const rate = Number(r.unit_cost ?? 0);
    return {
      Date: r.created_at,
      Branch: r.branch?.name ?? "",
      Department: r.department?.name ?? "",
      "Asset Code": r.work_order?.asset?.asset_code ?? "",
      "Asset Name": r.work_order?.asset?.name ?? "",
      "Work Order No": r.work_order?.wo_number ?? "",
      "Spare Code": r.spare?.code ?? "",
      "Spare Name": r.spare?.name ?? r.spare_name,
      "Net Used Qty": net,
      "Unit Rate": rate,
      "Net Cost": +(net * rate).toFixed(2),
    };
  }), [rows]);

  return (
    <ReportShell
      id="R-41" title="Spare Cost Report"
      description="Spare cost usage by branch, department, asset and Work Order."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="WO / asset / spare" />
          </div>
          <div className="flex items-end justify-end text-sm">
            <span className="text-muted-foreground">Total Net Cost:&nbsp;</span>
            <span className="font-semibold tabular-nums">{totalCost.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead>Asset</TableHead>
                <TableHead>WO #</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead className="text-right">Net Qty</TableHead>
                <TableHead className="text-right">Unit Rate</TableHead>
                <TableHead className="text-right">Net Cost</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const net = Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0);
                  const rate = Number(r.unit_cost ?? 0);
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">{r.created_at.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{r.branch?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs"><div className="font-medium">{r.work_order?.asset?.name}</div><div className="font-mono text-muted-foreground">{r.work_order?.asset?.asset_code}</div></TableCell>
                      <TableCell className="font-mono text-xs">{r.work_order?.wo_number}</TableCell>
                      <TableCell className="text-xs"><div className="font-medium">{r.spare?.name ?? r.spare_name}</div><div className="font-mono text-muted-foreground">{r.spare?.code}</div></TableCell>
                      <TableCell className="text-right tabular-nums">{net}</TableCell>
                      <TableCell className="text-right tabular-nums">{rate.toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums">{(net * rate).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} cost rows.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
